import { useWelcomeTourGuide } from "@app/components/assistant/WelcomeTourGuideProvider";
import { getAdminSectionHref } from "@app/components/navigation/config";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { NavigationSidebar } from "@app/components/navigation/NavigationSidebar";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useAuth } from "@app/lib/auth/AuthContext";
import { useAppRouter } from "@app/lib/platform";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { TRACKING_AREAS, trackEvent } from "@app/lib/tracking";
import { hasGroupManagementScope } from "@app/types/api/auth_context";
import type { SubscriptionType } from "@app/types/plan";
import { getWorkspaceDefaultAgentId } from "@app/types/user";
import type { WorkspaceType } from "@app/types/user";
import {
  AzimutAgent,
  AzimutCollaboration,
  AzimutConversation,
  AzimutModuleRail,
  AzimutRegion,
  AzimutSettings,
  LayoutLeft,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@dust-tt/sparkle";
import type { AzimutNavigationItem } from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import type React from "react";
import { useContext, useRef } from "react";

interface NavigationProps {
  hideSidebar: boolean;
  owner: WorkspaceType;
  subscription: SubscriptionType;
  navChildren?: React.ReactNode;
  subNavigation?: SidebarNavigation[] | null;
  isNavigationBarOpen: boolean;
  setNavigationBarOpen: (isOpen: boolean) => void;
  isFullScreen: boolean;
  isMobile: boolean;
}

export function Navigation({
  hideSidebar,
  owner,
  subscription,
  navChildren,
  subNavigation,
  isNavigationBarOpen,
  setNavigationBarOpen,
  isFullScreen,
  isMobile,
}: NavigationProps) {
  const { t } = useLingui();
  const router = useAppRouter();
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const { featureFlags, groupManagement } = useAuth();
  const { hasPermission } = useWorkspacePermissions();
  const { spaceMenuButtonRef } = useWelcomeTourGuide();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeMobileNavigation = () => {
    setSidebarOpen(false);
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-azimut-navigation-trigger] button")
        ?.focus();
    });
  };
  const adminHref = getAdminSectionHref(
    owner,
    hasPermission,
    featureFlags.includes("group_management") &&
      hasGroupManagementScope(groupManagement?.read_usage)
  );
  const path = router.asPath.split(/[?#]/)[0];
  const prefix = `/w/${owner.sId}`;
  const items: AzimutNavigationItem[] = [
    {
      id: "conversations",
      label: t`Conversations`,
      icon: AzimutConversation,
      href: `${prefix}/conversation/new?agent=${encodeURIComponent(getWorkspaceDefaultAgentId(owner) ?? "azimut")}`,
      selected:
        path.includes("/conversation/") || path.endsWith("/get-started"),
    },
    {
      id: "collaboration",
      label: t`Workrooms`,
      icon: AzimutCollaboration,
      href: `${prefix}/pods`,
      selected: path.includes("/pods"),
    },
    {
      id: "agents",
      label: t`Agents`,
      icon: AzimutAgent,
      href: `${prefix}/builder/agents`,
      selected: path.includes("/builder/"),
    },
    {
      id: "regions",
      label: t`Domains`,
      icon: AzimutRegion,
      href: `${prefix}/spaces`,
      selected: path.includes("/spaces"),
      containerRef: spaceMenuButtonRef,
    },
    ...(adminHref
      ? [
          {
            id: "settings",
            label: t`Settings`,
            icon: AzimutSettings,
            href: adminHref,
            selected: !!subNavigation?.some(
              (nav) =>
                nav.id === "organization" ||
                nav.id === "platform" ||
                nav.id === "spend"
            ),
          },
        ]
      : []),
  ];
  if (hideSidebar || isFullScreen) {
    return null;
  }
  const rail = (
    <AzimutModuleRail
      label={t`Workspace navigation`}
      items={items}
      onNavigate={(event, item) => {
        if (
          !item.href ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        trackEvent({
          area: TRACKING_AREAS.NAVIGATION,
          object: "workspace_module",
          extra: { module: item.id },
        });
        if (!isMobile) {
          setSidebarOpen(false);
        }
        void router.push(item.href);
      }}
      footer={
        !isMobile && (
          <button
            type="button"
            ref={toggleRef}
            className="az-context-toggle"
            aria-label={
              isNavigationBarOpen
                ? t`Hide context navigation`
                : t`Show context navigation`
            }
            aria-expanded={isNavigationBarOpen}
            aria-controls="azimut-context"
            onClick={() => {
              trackEvent({
                area: TRACKING_AREAS.NAVIGATION,
                object: "context_navigation_toggle",
              });
              setNavigationBarOpen(!isNavigationBarOpen);
            }}
          >
            <LayoutLeft className="h-5 w-5" />
          </button>
        )
      }
    />
  );
  const context = (
    <NavigationSidebar
      owner={owner}
      subscription={subscription}
      subNavigation={subNavigation}
      isMobile={isMobile}
      onHideContext={() => {
        if (isMobile) {
          closeMobileNavigation();
        } else {
          setNavigationBarOpen(false);
          toggleRef.current?.focus();
        }
      }}
    >
      {navChildren}
    </NavigationSidebar>
  );
  return (
    <div className="az-navigation">
      {isMobile ? (
        <Sheet
          open={sidebarOpen}
          onOpenChange={(open) =>
            open ? setSidebarOpen(true) : closeMobileNavigation()
          }
        >
          <SheetContent
            side="left"
            className="flex w-full max-w-[26rem] bg-panel-background p-0"
          >
            <SheetHeader className="sr-only" hideButton>
              <SheetTitle>{t`Workspace navigation`}</SheetTitle>
            </SheetHeader>
            <div className="az-navigation min-h-0 w-full flex-1">
              {rail}
              <div
                className="az-context-panel"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest("a[href]")) {
                    closeMobileNavigation();
                  }
                }}
              >
                {context}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <>
          {rail}
          <aside
            id="azimut-context"
            className="az-context-panel"
            data-collapsed={!isNavigationBarOpen}
          >
            {context}
          </aside>
        </>
      )}
    </div>
  );
}
